import React from 'react';
import { View, ViewProps, StyleProp, ViewStyle } from 'react-native';
import { useTheme } from './ThemeContext';
import type { Theme } from './themes';

/**
 * Windows 95 3D edges.
 *
 * The real thing draws two concentric 1px rectangles, each with a light
 * top/left and a dark bottom/right. Faking it with a single border loses the
 * characteristic double bevel, so this renders the outer and inner edges as
 * genuinely nested views.
 *
 *   raised   buttons, panels, the window frame itself
 *   sunken   text fields, list boxes, status-bar wells
 *   pressed  a raised control being held down (inverted, content shifts 1px)
 *   thin     single-line etched divider, used inside menus and group boxes
 */
export type BevelVariant = 'raised' | 'sunken' | 'pressed' | 'thin' | 'flat';

interface EdgeColors {
  outerTL: string;
  outerBR: string;
  innerTL: string;
  innerBR: string;
}

function edgesFor(theme: Theme, variant: Exclude<BevelVariant, 'flat'>): EdgeColors {
  const c = theme.colors;
  switch (variant) {
    case 'raised':
      return { outerTL: c.highlight, outerBR: c.darkShadow, innerTL: c.light, innerBR: c.shadow };
    case 'sunken':
      return { outerTL: c.shadow, outerBR: c.highlight, innerTL: c.darkShadow, innerBR: c.light };
    case 'pressed':
      return { outerTL: c.darkShadow, outerBR: c.highlight, innerTL: c.shadow, innerBR: c.light };
    case 'thin':
      return { outerTL: c.shadow, outerBR: c.highlight, innerTL: 'transparent', innerBR: 'transparent' };
  }
}

export interface BevelProps extends ViewProps {
  variant?: BevelVariant;
  /** Background applied to the inner content area. Defaults to the 3D face. */
  background?: string;
  contentStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}

export function Bevel({
  variant = 'raised',
  background,
  style,
  contentStyle,
  children,
  ...rest
}: BevelProps) {
  const theme = useTheme();
  const bg = background ?? theme.colors.face;
  const radius = theme.metrics.radius;

  if (variant === 'flat') {
    return (
      <View style={[{ backgroundColor: bg, borderRadius: radius }, style]} {...rest}>
        {children}
      </View>
    );
  }

  const e = edgesFor(theme, variant);
  const single = variant === 'thin';

  return (
    <View
      style={[
        {
          borderWidth: 1,
          borderRadius: radius,
          borderTopColor: e.outerTL,
          borderLeftColor: e.outerTL,
          borderBottomColor: e.outerBR,
          borderRightColor: e.outerBR,
        },
        style,
      ]}
      {...rest}
    >
      <View
        style={[
          single
            ? { borderWidth: 0 }
            : {
                borderWidth: 1,
                borderTopColor: e.innerTL,
                borderLeftColor: e.innerTL,
                borderBottomColor: e.innerBR,
                borderRightColor: e.innerBR,
              },
          { flex: 1, backgroundColor: bg, borderRadius: Math.max(0, radius - 1) },
          contentStyle,
        ]}
      >
        {children}
      </View>
    </View>
  );
}
